import { useState, useRef, useCallback } from 'react';

/**
 * Maximum pull distance in pixels. The pull gesture is clamped to this value.
 * @type {number}
 */
const MAX_PULL_DISTANCE = 120;

/**
 * Minimum pull distance in pixels required to trigger a refresh.
 * @type {number}
 */
const REFRESH_THRESHOLD = 60;

/**
 * Hook que encapsula la lógica de pull-to-refresh con touch events.
 * Solo se activa cuando el scroll está en la posición superior (scrollTop === 0)
 * y el gesto es hacia abajo.
 *
 * @param {() => Promise<void>} onRefresh - Función async que recarga los datos.
 * @returns {{ pullDistance: number, isRefreshing: boolean, handlers: { onTouchStart: Function, onTouchMove: Function, onTouchEnd: Function } }}
 */
export const usePullToRefresh = (onRefresh) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startYRef = useRef(0);
  const isTrackingRef = useRef(false);

  const onTouchStart = useCallback(
    (e) => {
      if (isRefreshing) return;

      // Only activate when scroll is at the top
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      if (scrollTop !== 0) return;

      startYRef.current = e.touches[0].clientY;
      isTrackingRef.current = true;
    },
    [isRefreshing]
  );

  const onTouchMove = useCallback(
    (e) => {
      if (!isTrackingRef.current || isRefreshing) return;

      const currentY = e.touches[0].clientY;
      const distance = currentY - startYRef.current;

      // Only track downward gestures
      if (distance <= 0) {
        setPullDistance(0);
        return;
      }

      // Clamp to max distance
      const clampedDistance = Math.min(distance, MAX_PULL_DISTANCE);
      setPullDistance(clampedDistance);
    },
    [isRefreshing]
  );

  const onTouchEnd = useCallback(async () => {
    if (!isTrackingRef.current || isRefreshing) return;

    isTrackingRef.current = false;

    if (pullDistance > REFRESH_THRESHOLD && onRefresh) {
      setIsRefreshing(true);
      setPullDistance(0);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, isRefreshing, onRefresh]);

  return {
    pullDistance,
    isRefreshing,
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
    },
  };
};
